import { useRef } from 'react'

import * as d3 from 'd3'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'

import { getCenteredCoordinates, getTransitionDuration } from 'modules/org_chart/lib/coordinates'
import { boundsContain, computeVisibleBounds } from 'modules/org_chart/lib/visibleBounds'
import type { Coordinates } from 'modules/org_chart/models/Coordinates'
import type { Dimensions } from 'modules/org_chart/models/Dimensions'
import type { PointNode } from 'modules/org_chart/models/PointNode'
import type { VisibleBounds } from 'modules/org_chart/models/VisibleBounds'

interface Return {
  scale: (ammount: number) => void
  navigateToNode: (node: PointNode, delay?: number) => void
  navigateToCoordinates: (x: number, y: number, delay?: number, avoidTransition?: boolean) => void
  reset: () => void
  isReady?: boolean
  scaleRange: {
    MIN_ZOOM: number
    MAX_ZOOM: number
  }
}

interface Params {
  svgElement: SVGSVGElement | null
  contentElement: SVGGElement | null
  size: Dimensions
  initialCoordinates: Coordinates
  /**
   * Called when the visible content bounds change by more than one viewport.
   * Coordinates are in the same space as `PointNode.x`/`PointNode.y`.
   * The hook throttles notifications so that panning inside the returned
   * bounds (which include a 1-viewport margin) does NOT trigger React state
   * updates. This lets the consumer cull off-viewport tree nodes without
   * paying for a re-render on every pan frame.
   */
  onVisibleBoundsChange?: (bounds: VisibleBounds) => void
}

const MIN_ZOOM = 0.37
const MAX_ZOOM = 1
const INITIAL_TRANSFORM = new d3.ZoomTransform(1, 0, 0)

/**
 * Expanding the viewport by one full viewport on each side means the user
 * has to pan an entire screen before we re-evaluate which nodes are visible.
 * A larger margin = fewer React re-renders during aggressive panning; a
 * smaller margin = fewer nodes rendered at any given time.
 */
const VIEWPORT_MARGIN_FACTOR = 1

const useZoom = ({
  svgElement,
  contentElement,
  size,
  initialCoordinates,
  onVisibleBoundsChange,
}: Params): Return => {
  const [width, height] = size
  const zoom = useMemo(() => d3.zoom<SVGSVGElement, unknown>(), [])
  const animationFrameRef = useRef<number>()
  const pendingTransformRef = useRef(INITIAL_TRANSFORM)
  const zoomStateRef = useRef(INITIAL_TRANSFORM)
  /**
   * The bounds we last notified the consumer about. These are already
   * padded by `VIEWPORT_MARGIN_FACTOR`, so we only notify again when the
   * *visible* (non-padded) viewport has left these bounds.
   */
  const notifiedBoundsRef = useRef<VisibleBounds | null>(null)
  const onVisibleBoundsChangeRef = useRef(onVisibleBoundsChange)
  onVisibleBoundsChangeRef.current = onVisibleBoundsChange

  const getDistance = (x: number, y: number) => {
    const a = x - zoomStateRef.current.x
    const b = y - zoomStateRef.current.y

    return Math.sqrt(a * a + b * b)
  }

  interface ZoomEvent {
    transform: d3.ZoomTransform
  }

  const applyTransform = useUnsafeCallback(
    (transform: d3.ZoomTransform) => {
      if (!contentElement) {
        return
      }

      contentElement.style.transformOrigin = ''
      contentElement.style.transform = ''
      contentElement.setAttribute('transform', transform.toString())
    },
    [contentElement]
  )

  const maybeNotifyVisibleBounds = useUnsafeCallback(
    (transform: d3.ZoomTransform) => {
      const cb = onVisibleBoundsChangeRef.current
      if (!cb) return

      // Bounds of the actually-visible viewport (no margin).
      const visible: VisibleBounds = {
        minX: -transform.x / transform.k,
        minY: -transform.y / transform.k,
        maxX: (-transform.x + width) / transform.k,
        maxY: (-transform.y + height) / transform.k,
      }

      // If the visible area is still inside what we last notified, skip.
      if (notifiedBoundsRef.current && boundsContain(notifiedBoundsRef.current, visible)) {
        return
      }

      const padded = computeVisibleBounds(transform, [width, height], VIEWPORT_MARGIN_FACTOR)
      notifiedBoundsRef.current = padded
      cb(padded)
    },
    [height, width]
  )

  const handleZoomChange = useUnsafeCallback(
    (zoomEvent: ZoomEvent) => {
      if (!contentElement) return

      const { transform } = zoomEvent
      pendingTransformRef.current = transform

      if (animationFrameRef.current) {
        return
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        const nextTransform = pendingTransformRef.current
        zoomStateRef.current = nextTransform
        applyTransform(nextTransform)
        maybeNotifyVisibleBounds(nextTransform)
        animationFrameRef.current = undefined
      })
    },
    [applyTransform, contentElement, maybeNotifyVisibleBounds]
  )

  const scale = (amount: number) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    zoom.scaleBy(svg.transition().duration(getTransitionDuration()), amount)
  }

  const navigateToCoordinates = (x: number, y: number, delay = 0, avoidTransition?: boolean) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    const [centeredX, centeredY] = getCenteredCoordinates(x, y, size)
    const translation = d3.zoomIdentity.translate(-centeredX, -centeredY).scale(1)
    const distance = getDistance(centeredX, centeredY)
    const transitionDuration = avoidTransition ? 0 : getTransitionDuration(distance)

    zoom.transform(svg.transition().delay(delay).duration(transitionDuration), translation)
  }

  const navigateToNode = (pointNode: PointNode, delay?: number) => {
    const { x, y } = pointNode

    navigateToCoordinates(x, y, delay)
  }

  const reset = () => {
    const [initialX, initialY] = initialCoordinates

    navigateToCoordinates(initialX, initialY)
  }

  useUnsafeEffect(() => {
    if (!svgElement || !contentElement) return

    const svg = d3.select(svgElement)

    zoom.on('zoom', handleZoomChange)
    zoom.scaleExtent([MIN_ZOOM, MAX_ZOOM])

    svg.call(zoom).on('dblclick.zoom', null)
    applyTransform(zoomStateRef.current)
    maybeNotifyVisibleBounds(zoomStateRef.current)

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = undefined
      }

      svg.on('.zoom', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyTransform, contentElement, svgElement, maybeNotifyVisibleBounds])

  return {
    scale,
    navigateToNode,
    navigateToCoordinates,
    reset,
    scaleRange: { MIN_ZOOM, MAX_ZOOM },
    isReady: !!svgElement && !!contentElement,
  }
}

export default useZoom
